import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MessageCircle, Mail, CheckCircle2, Clock, Users, Layout } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND, CONTACT } from "@/data/translations";
import { LangToggle } from "@/components/LangToggle";

const ProtocolNotFound = ({ t }) => (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-5">
        <div className="text-center">
            <p className="font-heavy text-4xl md:text-6xl uppercase mb-6">{t.protocolPage.notFound}</p>
            <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-fw text-black font-display font-semibold text-[11px] uppercase tracking-[0.18em] rounded-sm"
            >
                <ArrowLeft className="w-4 h-4" strokeWidth={2} />
                {t.protocolPage.backHome}
            </Link>
        </div>
    </div>
);

const ProtocolPageHeader = ({ t }) => (
    <header className="border-b border-white/10 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-5 flex items-center justify-between">
            <Link to="/" data-testid="protocol-back-link" className="flex items-center gap-3 group">
                <picture>
                    <source srcSet={BRAND.logoDarkWebp} type="image/webp" />
                    <img src={BRAND.logoDark} alt="Fit & Wellness" className="h-9 md:h-10 w-auto" decoding="async" width="120" height="40" />
                </picture>
            </Link>
            <div className="flex items-center gap-4">
                <LangToggle testid="protocol-language-toggle" />
                <Link
                    to="/#protocols"
                    className="hidden sm:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/70 hover:text-fw transition-colors"
                >
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                    {t.protocolPage.backToProtocols}
                </Link>
            </div>
        </div>
    </header>
);

const ProtocolInfoGrid = ({ detail, t }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-16">
        <div className="bg-black p-7 md:p-9">
            <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-fw" strokeWidth={2} />
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw">
                    {t.protocolPage.forWho}
                </span>
            </div>
            <p data-testid="protocol-for-who" className="text-base text-white/80 leading-relaxed">
                {detail.forWho}
            </p>
        </div>

        <div className="bg-black p-7 md:p-9">
            <div className="flex items-center gap-2 mb-4">
                <Layout className="w-4 h-4 text-fw" strokeWidth={2} />
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw">
                    {t.protocolPage.format}
                </span>
            </div>
            <p className="text-base text-white/80 leading-relaxed mb-5">
                {detail.format}
            </p>
            <div className="flex items-center gap-2 mb-3 mt-6">
                <Clock className="w-4 h-4 text-fw" strokeWidth={2} />
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw">
                    {t.protocolPage.duration}
                </span>
            </div>
            <p className="text-base text-white/80 leading-relaxed">
                {detail.duration}
            </p>
        </div>
    </div>
);

const ProtocolIncludes = ({ items, t }) => (
    <section className="mb-20">
        <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-px bg-fw" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-fw">
                {t.protocolPage.includes}
            </span>
        </div>
        <ul data-testid="protocol-includes" className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
            {items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base md:text-lg text-white/85 bg-white/5 border border-white/10 p-5">
                    <CheckCircle2 className="w-5 h-5 text-fw mt-0.5 shrink-0" strokeWidth={2} />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    </section>
);

const ProtocolCTABanner = ({ t }) => (
    <section className="relative overflow-hidden bg-fw text-black p-8 md:p-12 lg:p-16 rounded-sm">
        <div className="absolute -top-16 -right-16 w-[280px] h-[280px] rounded-full bg-fw-glow/40 blur-3xl pointer-events-none" />
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
                <h2 className="font-heavy text-3xl md:text-4xl lg:text-5xl leading-[0.95] uppercase mb-4 text-balance">
                    {t.protocolPage.ctaTitle}
                </h2>
                <p className="text-base md:text-lg text-black/85 leading-relaxed max-w-2xl">
                    {t.protocolPage.ctaText}
                </p>
            </div>
            <div className="lg:col-span-5 flex flex-col gap-3">
                <a
                    href={CONTACT.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="protocol-cta-whatsapp"
                    className="group flex items-center justify-between gap-3 px-6 py-4 bg-black text-white font-display font-semibold rounded-sm hover:bg-ink-700 transition-all duration-300"
                >
                    <span className="flex items-center gap-3">
                        <MessageCircle className="w-5 h-5" strokeWidth={2} />
                        <span className="text-sm uppercase tracking-[0.18em]">
                            {t.protocolPage.ctaWhatsapp}
                        </span>
                    </span>
                    <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={2} />
                </a>
                <a
                    href={CONTACT.emailUrl}
                    data-testid="protocol-cta-email"
                    className="group flex items-center justify-between gap-3 px-6 py-4 border border-black/40 text-black font-display font-semibold rounded-sm hover:border-black hover:bg-black hover:text-white transition-all duration-300"
                >
                    <span className="flex items-center gap-3">
                        <Mail className="w-5 h-5" strokeWidth={2} />
                        <span className="text-sm uppercase tracking-[0.18em]">
                            {t.protocolPage.ctaEmail}
                        </span>
                    </span>
                    <ArrowUpRight className="w-5 h-5" strokeWidth={2} />
                </a>
            </div>
        </div>
    </section>
);

export const Protocol = () => {
    const { slug } = useParams();
    const { t } = useLanguage();
    const protocol = t.protocols.find((p) => p.slug === slug);

    if (!protocol) {
        return <ProtocolNotFound t={t} />;
    }

    const { detail } = protocol;

    return (
        <div className="min-h-screen bg-black text-white">
            <ProtocolPageHeader t={t} />

            <main className="mx-auto max-w-[1100px] px-5 md:px-10 py-14 md:py-20">
                <div className="flex items-baseline gap-3 mb-6">
                    <span className="font-display font-bold text-3xl md:text-4xl text-fw tabular-nums leading-none">
                        {protocol.id}
                    </span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/55">
                        {t.protocolPage.protocolNumber}
                    </span>
                </div>

                <h1
                    data-testid="protocol-title"
                    className="font-heavy text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] uppercase mb-6 text-balance"
                >
                    {protocol.name}
                </h1>

                <p className="font-display italic text-lg md:text-xl text-fw mb-12 max-w-3xl">
                    {protocol.tagline}
                </p>

                <p data-testid="protocol-intro" className="text-base md:text-lg text-white/80 leading-relaxed max-w-3xl mb-16">
                    {detail.intro}
                </p>

                <ProtocolInfoGrid detail={detail} t={t} />
                <ProtocolIncludes items={detail.includes} t={t} />
                <ProtocolCTABanner t={t} />

                <div className="mt-16 pt-10 border-t border-white/10">
                    <Link
                        to="/#protocols"
                        data-testid="protocol-back-bottom"
                        className="inline-flex items-center gap-3 px-6 py-3.5 border border-white/30 text-white font-display font-semibold text-[11px] uppercase tracking-[0.18em] rounded-sm hover:border-fw hover:text-fw transition-all duration-300"
                    >
                        <ArrowLeft className="w-4 h-4" strokeWidth={2} />
                        {t.protocolPage.backToProtocols}
                    </Link>
                </div>
            </main>
        </div>
    );
};
